import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Users, 
  Wrench, 
  DollarSign, 
  Clock, 
  Target,
  ArrowRight,
  TrendingUp,
  BarChart3,
  Shield,
  Zap,
  CheckCircle
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const AssetsLanding = () => {
  const navigate = useNavigate();

  const assetCards = [
    {
      id: 'asset-management',
      title: 'Asset Management',
      description: 'Comprehensive asset tracking & management system',
      icon: <Package size={24} />,
      path: '/assets/management',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Asset tracking', 'Inventory management', 'Asset details']
    },
    {
      id: 'assignment-usage',
      title: 'Assignment & Usage',
      description: 'Track asset assignments & usage patterns',
      icon: <Users size={24} />,
      path: '/assets/assignment',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Check-in/check-out', 'Allocation tracking', 'Usage analytics']
    },
    {
      id: 'maintenance-ops',
      title: 'Maintenance & Ops',
      description: 'Preventive & corrective maintenance management',
      icon: <Wrench size={24} />,
      path: '/assets/maintenance',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Preventive maintenance', 'Corrective maintenance', 'Work orders']
    },
    {
      id: 'financial-valuation',
      title: 'Financial & Valuation',
      description: 'Asset depreciation, valuation & insurance tracking',
      icon: <DollarSign size={24} />,
      path: '/assets/financial',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Depreciation tracking', 'Valuation reports', 'Insurance management']
    },
    {
      id: 'asset-lifecycle',
      title: 'Asset Lifecycle',
      description: 'Complete asset lifecycle from acquisition to disposal',
      icon: <Clock size={24} />,
      path: '/assets/lifecycle',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Acquisition tracking', 'Lifecycle stages', 'Disposal management']
    },
    {
      id: 'asset-categorization',
      title: 'Asset Categorization',
      description: 'Organize assets by type, category & classification',
      icon: <Target size={24} />,
      path: '/assets/categorization',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['IT equipment', 'Real estate', 'Licenses', 'Categories']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Assets Management"
        description="Comprehensive asset lifecycle management and tracking system"
      />
      <div className="page-container">
        {/* Asset Management Cards */}
        <div 
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {assetCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--primary-color)`,
                  width: '100%'
                }}
              />
              
              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: card.iconLine }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="asset-card-arrow"
                  />
                </div>
                
                {/* Description */}
                <p style={{ 
                  fontSize: '13px', 
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>
                
                {/* Features */}
                <div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '500', 
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '11px',
                          padding: '3px 6px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AssetsLanding;
